/**
 * rutasPublicas.js — la tabla de URLs públicas por idioma. Fuente ÚNICA.
 *
 * ── POR QUÉ ESTO EXISTE ─────────────────────────────────────────────────────
 *
 * Hasta ahora una página pública tenía una sola URL y el idioma lo decidía el
 * navegador. Para Google eso significa que de cada página solo existe UNA
 * versión indexable, y es la que le toque al crawler — que renderiza con
 * `en-US`, así que desde que hay inglés traducido vería el sitio en inglés.
 *
 * A partir de aquí manda la URL: `/visibilidad-seo` es española y
 * `/de/seo-sichtbarkeit` es alemana, venga quien venga y tenga lo que tenga en
 * `localStorage`.
 *
 * De esta tabla beben CINCO consumidores: el router, el conmutador de idioma,
 * el `<head>` (canonical + hreflang), el generador de `sitemap.xml` y el script
 * de prerender. Cada uno con su propia lista sería garantía de que el sitemap
 * anuncia una URL que el router no sirve. Con una sola, no se puede.
 *
 * ── LAS TRES REGLAS QUE NO SE NEGOCIAN ──────────────────────────────────────
 *
 * 1. **El español va en la raíz, sin prefijo.** No por gusto: `mytapstar.com`
 *    ya tiene posiciones en Search Console y mover una URL que rankea cuesta
 *    dinero. Por eso `/pricing` sigue diciendo «pricing» aunque sea la versión
 *    española — el slug está en inglés, pero es el que Google conoce.
 * 2. **Los slugs de los prefijos SÍ van traducidos**, porque son URLs nuevas
 *    sin historial que perder y la palabra clave en la URL suma.
 * 3. **Solo ASCII en los slugs.** Nada de `ç`, `ã`, `ñ` ni acentos: una URL con
 *    caracteres no-ASCII se percent-encodea al copiarla y acaba compartida como
 *    `/pt/multilocaliza%C3%A7%C3%A3o`.
 *
 * ── EL PELIGRO DE `/en` ─────────────────────────────────────────────────────
 *
 * `/en1/<id>` es la ruta pública del LinkTree, y empieza por «en». Esto ya
 * rompió producción una vez: un `location ~ ^/(api|admin|…|en|…)` sin
 * delimitador se tragaba `/en1/...` y devolvía un 404 de Django
 * (`decisiones.md` §52). Todo lo que reconozca un prefijo aquí ancla en el
 * separador —`=== '/en'` o `startsWith('/en/')`— y NUNCA hace
 * `startsWith('/en')`. El test lo comprueba con `/en1/abc`.
 */
import { SOBRE_NOSOTROS_HABILITADA } from './sobreNosotrosGate';

/** Los cinco que sirve el SPA, en orden de importancia para el negocio. */
export const IDIOMAS = ['es', 'pt', 'en', 'de', 'fr'];

/** El idioma sin prefijo: vive en la raíz y es el `x-default` de los hreflang. */
export const IDIOMA_RAIZ = 'es';

/**
 * Los que entran en `sitemap.xml`.
 *
 * Alemán y francés tienen URL y `hreflang` —para que Google sepa que existen y
 * a quién servírselas— pero no se pide su rastreo: no son mercados en los que
 * se vaya a invertir en contenido, y un sitemap lleno de páginas que nadie va a
 * trabajar solo diluye el presupuesto de rastreo.
 */
export const IDIOMAS_INDEXABLES = ['es', 'pt', 'en'];

/**
 * Las páginas públicas de marketing, con su URL en cada idioma.
 *
 * La clave es interna y no aparece nunca en una URL: es lo que usan el router y
 * el copy para referirse a la página sin depender de su slug. Cambiar un slug
 * es entonces tocar una línea aquí; cambiar una clave es otra cosa y rompe a
 * propósito.
 *
 * ⚠️ Las páginas legales (`/terms`, `/privacy`, `/dpa`, `/legal-notice`,
 * `/cookies`) NO están aquí a propósito: no son páginas de captación, no se van
 * a traducir por URL y moverlas solo tiene riesgo. Se quedan donde están y
 * fuera del sitemap.
 */
export const PAGINAS = {
  home: {
    es: '/',
    pt: '/pt/',
    en: '/en/',
    de: '/de/',
    fr: '/fr/',
  },
  multiubicaciones: {
    es: '/multiubicaciones',
    pt: '/pt/multilocalizacao',
    en: '/en/multi-location',
    de: '/de/mehrere-standorte',
    fr: '/fr/multi-etablissements',
  },
  visibilidadSeo: {
    es: '/visibilidad-seo',
    pt: '/pt/visibilidade-seo',
    en: '/en/seo-visibility',
    de: '/de/seo-sichtbarkeit',
    fr: '/fr/visibilite-seo',
  },
  automatizaciones: {
    es: '/automatizaciones',
    pt: '/pt/automatizacoes',
    en: '/en/automations',
    de: '/de/automatisierungen',
    fr: '/fr/automatisations',
  },
  publicacionesIa: {
    es: '/publicaciones-ia',
    pt: '/pt/publicacoes-ia',
    en: '/en/ai-posts',
    de: '/de/ki-beitraege',
    fr: '/fr/publications-ia',
  },
  widgetResenas: {
    es: '/widget-resenas',
    pt: '/pt/widget-avaliacoes',
    en: '/en/reviews-widget',
    de: '/de/bewertungs-widget',
    fr: '/fr/widget-avis',
  },
  restauracion: {
    es: '/restauracion',
    pt: '/pt/restauracao',
    en: '/en/restaurants',
    de: '/de/gastronomie',
    fr: '/fr/restauration',
  },
  clinicas: {
    es: '/clinicas',
    pt: '/pt/clinicas',
    en: '/en/clinics',
    de: '/de/praxen',
    fr: '/fr/cliniques',
  },
  automotriz: {
    es: '/automotriz',
    pt: '/pt/automovel',
    en: '/en/automotive',
    de: '/de/kfz',
    fr: '/fr/automobile',
  },
  hoteles: {
    es: '/hoteles',
    pt: '/pt/hoteis',
    en: '/en/hotels',
    de: '/de/hotels',
    // Coincide con el alemán, y no pasa nada: el prefijo ya las distingue.
    fr: '/fr/hotels',
  },

  /*
   * Precios.
   *
   * ⚠️ El slug español es `/pricing`, en inglés, y es a propósito: es la URL que
   * la V2 ya tenía indexada y con posiciones en Search Console. La regla 1 de la
   * cabecera manda sobre la coherencia de los slugs.
   *
   * Esto REVIERTE la redirección `/pricing → /#precios` que se puso al retirar
   * la V2 (`be8fa0d87`). Entonces la tabla de precios solo vivía en la home y
   * mandar la URL allí era lo correcto; ahora vuelve a haber página, así que
   * redirigir sería quitarle a esa URL justo el contenido que la hizo rankear.
   *
   * La home CONSERVA su bloque `#precios`: el ancla sigue funcionando y quien
   * llega a la home no tiene que salir de ella para ver la tabla.
   *
   * La página se enlaza desde el menú y el pie de la landing (antes no, por
   * decisión de producto; se recuperó en octubre de 2026). Con sesión no pinta
   * la tabla sino el checkout (`pages/v3/PreciosSegunSesion.jsx`).
   */
  precios: {
    es: '/pricing',
    pt: '/pt/precos',
    en: '/en/pricing',
    de: '/de/preise',
    fr: '/fr/tarifs',
  },

  /*
   * Sobre nosotros. Página institucional enlazada desde el pie. Indexable y en
   * el sitemap como las demás: es la que Google enseña cuando alguien busca la
   * marca junto a «empresa», «equipo» o «Valencia», y la que da contexto E-E-A-T
   * al resto del sitio.
   */
  sobreNosotros: {
    es: '/sobre-nosotros',
    pt: '/pt/sobre-nos',
    en: '/en/about-us',
    de: '/de/ueber-uns',
    fr: '/fr/a-propos',
  },
};

// «Sobre nosotros» todavía no sale en producción: ver `lib/sobreNosotrosGate.js`.
// Se quita AQUÍ y no en cada consumidor para que router, prerender, sitemap y
// pie no puedan discrepar: si no está en la tabla, no existe para ninguno.
if (!SOBRE_NOSOTROS_HABILITADA) delete PAGINAS.sobreNosotros;

/** Las claves de `PAGINAS`, en el orden en que están escritas. */
export const CLAVES_PAGINA = Object.keys(PAGINAS);

/**
 * Normaliza un pathname para compararlo: minúsculas y sin barra final.
 *
 * La raíz se queda en `'/'` y no en `''`, a diferencia de `normalizePath` de
 * `authRedirect.js`, porque aquí `'/'` es una URL de verdad —la home española—
 * y no un caso especial.
 */
function normaliza(pathname) {
  const limpio = pathname.toLowerCase().replace(/\/+$/, '');
  return limpio || '/';
}

/**
 * Un pathname de verdad: cadena que empieza por `/`.
 *
 * Existe para que `''` y `undefined` NO se confundan con la raíz. Si un
 * `location.pathname` llegara vacío por lo que sea, tratarlo como la home
 * española le pondría el panel en español a un cliente alemán, y sin ruido: es
 * exactamente el fallo que no se reporta porque parece una preferencia.
 */
function rutaValida(pathname) {
  return typeof pathname === 'string' && pathname.startsWith('/');
}

/** Índice inverso `url normalizada → { pagina, idioma }`, construido una vez. */
const PORURL = new Map();
for (const [pagina, porIdioma] of Object.entries(PAGINAS)) {
  for (const [idioma, url] of Object.entries(porIdioma)) {
    PORURL.set(normaliza(url), { pagina, idioma });
  }
}

/** La URL de una página en un idioma. `null` si no existe la combinación. */
export function urlDe(pagina, idioma) {
  return PAGINAS[pagina]?.[idioma] ?? null;
}

/**
 * El idioma que impone la URL, o `null` si la URL no manda.
 *
 * Devolver `null` es la mitad importante de esta función. Pasa en todo lo que
 * NO es una página pública de marketing: el panel con sesión, `/login`, los
 * `/en1/<id>` de LinkTree, los `/redirect/?id=` de los QR impresos, `/join`…
 * Ahí el idioma lo sigue eligiendo el usuario como hasta hoy, y este módulo se
 * aparta. Si alguna vez devolviera `'es'` por defecto, a un cliente alemán se
 * le pondría el panel en español.
 */
export function idiomaDeLaRuta(pathname) {
  if (!rutaValida(pathname)) return null;
  const ruta = normaliza(pathname);

  const conocida = PORURL.get(ruta);
  if (conocida) return conocida.idioma;

  // Una página bajo un prefijo que no está en la tabla —una subruta futura, o
  // una URL vieja que redirige— sigue siendo de ese idioma. El `=== prefijo ||
  // startsWith(prefijo + '/')` es lo que impide que `/en1/abc` cuente como
  // inglés; ver la cabecera del fichero.
  for (const idioma of IDIOMAS) {
    if (idioma === IDIOMA_RAIZ) continue;
    const prefijo = `/${idioma}`;
    if (ruta === prefijo || ruta.startsWith(`${prefijo}/`)) return idioma;
  }

  return null;
}

/** La clave de página de una URL, o `null` si no es una página de la tabla. */
export function paginaDeLaRuta(pathname) {
  if (!rutaValida(pathname)) return null;
  return PORURL.get(normaliza(pathname))?.pagina ?? null;
}

/**
 * Las otras URLs de la misma página, para los `hreflang` y el conmutador de
 * idioma. Devuelve `[{ idioma, url }]` con los cinco, incluido el actual: el
 * `hreflang` tiene que ser autorreferente además de cruzado.
 */
export function alternativasDe(pagina) {
  const porIdioma = PAGINAS[pagina];
  if (!porIdioma) return [];
  return IDIOMAS.map((idioma) => ({ idioma, url: porIdioma[idioma] }));
}
